// src/services/vehicleSellService.js
import { supabase } from "../supabaseClient";

/* ============================================================
   CONSTANTS
   ============================================================ */
const BUCKET = "vehicle-images";
const TARGET_MAX_BYTES = 100 * 1024; // 100 KB
const MAX_DIMENSION = 1600;          // px — longest side after resize

/* ============================================================
   HELPERS
   ============================================================ */
const toNullIfEmpty = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const toIntOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : null;
};

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalizePhone = (v) => String(v || "").replace(/\D/g, "");

const extFromType = (type) => {
  if (!type) return "jpg";
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
};

const safeName = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/* ============================================================
   IMAGE COMPRESSION — target ~100KB
   ============================================================ */
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

/**
 * Compress an image file to ~100 KB.
 * Uses resize + JPEG quality stepping.
 */
export const compressImageToTarget = async (
  file,
  { targetBytes = TARGET_MAX_BYTES, maxDimension = MAX_DIMENSION } = {}
) => {
  if (!file || !file.type.startsWith("image/")) return file;

  // SVG and GIF — skip compression (canvas would break them)
  if (file.type.includes("svg") || file.type.includes("gif")) return file;

  const img = await loadImage(file);

  // Compute scaled dimensions
  let { width, height } = img;
  if (Math.max(width, height) > maxDimension) {
    const ratio = maxDimension / Math.max(width, height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  // Try progressively lower JPEG quality
  let quality = 0.85;
  let blob = await canvasToBlob(canvas, "image/jpeg", quality);

  while (blob && blob.size > targetBytes && quality > 0.25) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, "image/jpeg", quality);
  }

  // If still too large, shrink dimensions further
  let shrunkW = width;
  let shrunkH = height;
  while (blob && blob.size > targetBytes && Math.max(shrunkW, shrunkH) > 600) {
    shrunkW = Math.round(shrunkW * 0.8);
    shrunkH = Math.round(shrunkH * 0.8);

    const c2 = document.createElement("canvas");
    c2.width = shrunkW;
    c2.height = shrunkH;
    const ctx2 = c2.getContext("2d");
    ctx2.imageSmoothingEnabled = true;
    ctx2.imageSmoothingQuality = "high";
    ctx2.drawImage(img, 0, 0, shrunkW, shrunkH);

    blob = await canvasToBlob(c2, "image/jpeg", 0.75);
  }

  if (!blob) return file;

  return new File([blob], `${safeName()}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

/* ============================================================
   SERVICE
   ============================================================ */
export const vehicleSellService = {
  /* ----------------------------------------------------------
     UPLOAD IMAGES (with compression)
     Returns array of public URLs
  ---------------------------------------------------------- */
  async uploadImages(files, onProgress) {
    const urls = [];

    for (let i = 0; i < files.length; i++) {
      const original = files[i];
      const compressed = await compressImageToTarget(original);
      const path = `listings/${safeName()}.${extFromType(compressed.type)}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, compressed, {
          cacheControl: "31536000",
          upsert: false,
          contentType: compressed.type,
        });

      if (uploadError) throw uploadError;

      const { data: publicUrl } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path);

      urls.push(publicUrl.publicUrl);

      if (onProgress) onProgress(i + 1, files.length);
    }

    return urls;
  },

  /* ----------------------------------------------------------
     CREATE LISTING
  ---------------------------------------------------------- */
  async createVehicleSellRequest(payload, files, onProgress) {
    // 1. Upload images first
    let imageUrls = [];
    if (files && files.length) {
      imageUrls = await this.uploadImages(files, onProgress);
    }

    // 2. Build insert row
    const insertRow = {
      owner_name: String(payload.owner_name || "").trim(),
      phone: normalizePhone(payload.phone),
      whatsapp: toNullIfEmpty(payload.whatsapp ? normalizePhone(payload.whatsapp) : null),
      email: toNullIfEmpty(payload.email),
      city: toNullIfEmpty(payload.city),
      state: toNullIfEmpty(payload.state),

      vehicle_type: toNullIfEmpty(payload.vehicle_type),
      vehicle_brand: String(payload.vehicle_brand || "").trim(),
      vehicle_model: String(payload.vehicle_model || "").trim(),
      vehicle_variant: toNullIfEmpty(payload.vehicle_variant),
      manufacturing_year: toIntOrNull(payload.manufacturing_year),
      fuel_type: toNullIfEmpty(payload.fuel_type),
      transmission: toNullIfEmpty(payload.transmission),
      km_driven: toIntOrNull(payload.km_driven),
      owners_count: toIntOrNull(payload.owners_count),
      registration_number: toNullIfEmpty(
        payload.registration_number ? String(payload.registration_number).toUpperCase() : null
      ),
      registration_state: toNullIfEmpty(payload.registration_state),
      insurance_valid_till: toNullIfEmpty(payload.insurance_valid_till),
      color: toNullIfEmpty(payload.color),

      expected_price: toNumberOrNull(payload.expected_price),
      negotiable: payload.negotiable !== false,

      description: toNullIfEmpty(payload.description),

      images: imageUrls,

      status: "pending",
      is_published: false,
    };

    // 3. Validation
    if (!insertRow.owner_name) throw new Error("Owner name is required");
    if (!insertRow.phone || insertRow.phone.length < 10) {
      throw new Error("Enter a valid 10-digit mobile number");
    }
    if (!insertRow.vehicle_brand) throw new Error("Vehicle brand is required");
    if (!insertRow.vehicle_model) throw new Error("Vehicle model is required");
    if (!imageUrls.length) throw new Error("Please upload at least one image");

    // 4. Insert row
    const { data, error } = await supabase
      .from("vehicle_sell_requests")
      .insert([insertRow])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     PUBLIC LIST (published listings)
  ---------------------------------------------------------- */
  async listPublishedVehicles({ search, brand, limit = 100 } = {}) {
    let query = supabase
      .from("vehicle_sell_requests")
      .select("*")
      .eq("is_published", true)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (brand) query = query.eq("vehicle_brand", brand);

    if (search && search.trim()) {
      const s = `%${search.trim()}%`;
      query = query.or(
        `vehicle_brand.ilike.${s},vehicle_model.ilike.${s},vehicle_variant.ilike.${s},city.ilike.${s}`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /* ----------------------------------------------------------
     LIST ALL (for admin)
  ---------------------------------------------------------- */
  async listAllVehicles({ status, search, limit = 200 } = {}) {
    let query = supabase
      .from("vehicle_sell_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (status) query = query.eq("status", status);

    if (search && search.trim()) {
      const s = `%${search.trim()}%`;
      query = query.or(
        `owner_name.ilike.${s},phone.ilike.${s},vehicle_brand.ilike.${s},vehicle_model.ilike.${s},registration_number.ilike.${s}`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /* ----------------------------------------------------------
     GET ONE
  ---------------------------------------------------------- */
  async getVehicle(id) {
    const { data, error } = await supabase
      .from("vehicle_sell_requests")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     UPDATE (admin)
  ---------------------------------------------------------- */
  async updateVehicle(id, patch) {
    const { data, error } = await supabase
      .from("vehicle_sell_requests")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export default vehicleSellService;